import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import type { Option } from '@/screens/scan/lib/options'

/**
 * Multi-select dropdown — the add-in's stand-in for the web app's
 * DROPDOWN_MULTI fields (Type, State, Scope, Type/Sub-Type Of Insurance). Closes
 * on outside click; shows an inline search once the option list gets long. Each
 * selected value renders as a removable chip in the control.
 */
export function MultiSelect({
  placeholder,
  options,
  selected,
  onChange,
  disableWhenEmpty = true
}: {
  placeholder: string
  options: Option[]
  selected: string[]
  onChange: (next: string[]) => void
  /** When true (default), an empty option list is non-interactive ("None available"). */
  disableWhenEmpty?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((option) => option.label.toLowerCase().includes(q)) : options
  }, [options, query])

  const selectedSet = new Set(selected)
  const toggle = (value: string) => {
    const next = new Set(selected)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange([...next])
  }
  const labelFor = (value: string) => options.find((option) => option.value === value)?.label ?? value

  const disabled = disableWhenEmpty && options.length === 0

  return (
    <div className='ramp-ms' ref={ref}>
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        className={`ramp-ms-control${selected.length ? ' has-value' : ''}${disabled ? ' is-disabled' : ''}`}
        aria-expanded={open}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (disabled) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen((value) => !value)
          }
        }}
      >
        {disabled ? (
          <span className='ramp-ms-summary'>None available</span>
        ) : selected.length === 0 ? (
          <span className='ramp-ms-summary'>{placeholder}</span>
        ) : (
          <span className='ramp-ms-chips'>
            {selected.map((value) => (
              <span key={value} className='ramp-ms-chip'>
                <span className='ramp-ms-chip-label'>{labelFor(value)}</span>
                <button
                  type='button'
                  className='ramp-ms-chip-remove'
                  aria-label={`Remove ${labelFor(value)}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    toggle(value)
                  }}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </span>
        )}
        <ChevronDown size={14} className='ramp-ms-caret' />
      </div>
      {open && !disabled ? (
        <div className='ramp-ms-menu'>
          {options.length > 8 ? (
            <div className='ramp-ms-search'>
              <Search size={12} />
              <input
                autoFocus
                type='text'
                value={query}
                placeholder='Search…'
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          ) : null}
          <div className='ramp-ms-options'>
            {filtered.map((option) => {
              const on = selectedSet.has(option.value)
              return (
                <button
                  key={option.value}
                  type='button'
                  className={`ramp-ms-option${on ? ' is-on' : ''}`}
                  onClick={() => toggle(option.value)}
                >
                  <span className='ramp-ms-check'>{on ? <Check size={12} /> : null}</span>
                  <span className='ramp-ms-label'>{option.label}</span>
                </button>
              )
            })}
            {filtered.length === 0 ? <div className='ramp-ms-empty'>No matches</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
